import React, { useEffect, useCallback } from 'react'
import { useFormik } from 'formik'
import { object, string } from 'yup'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { auth, db } from '../firebase-config'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Textarea } from '../components/Textarea'
import { Error } from '../components/Error'
import { Category } from '../components/Category'
import { CATEGORIES } from '../Tags'
import categoryStyles from '../components/Category.module.css'
import styles from './EditProfile.module.css'

const requiredMsg = 'Dieses Feld ist verpflichtend'

const shopSchema = object({
  shopname: string().min(3, 'mindestens 3 Zeichen').required(requiredMsg),
  street: string().min(5, 'mindestens 5 Zeichen').required(requiredMsg),
  postcode: string().min(4, 'mindestens 4 Zeichen').required(requiredMsg),
  place: string().min(2, 'mindestens 2 Zeichen').required(requiredMsg),
  openingHours: string().min(2, 'mindestens 2 Zeichen').required(requiredMsg),
  phone: string().min(4, 'mindestens 4 Zeichen').nullable(),
  link: string().url('muss valider Link sein').nullable(),
})

const initialValues = {
  shopname: '',
  street: '',
  postcode: '',
  place: '',
  openingHours: '',
  phone: '',
  link: '',
  categories: [],
}

async function getGeo(streetname, postcode, place) {
  const searchText = `${streetname}%20${postcode}%20${place}`
  const data = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchText}.json?access_token=${mapboxgl.accessToken}`,
  )
  const parsedData = await data.json()
  return parsedData.features[0].center
}

export const EditShop = () => {

  const userRef = doc(db, 'users', auth.currentUser.uid)

  const formik = useFormik({
    initialValues,
    validationSchema: shopSchema,
    onSubmit: (values) => {
      handleSubmit(values)
    },
  })

  const getShop = useCallback(
    async function getShop() {
      const shop = await getDoc(userRef)
      formik.setValues(shop.data())
    },
    [formik, userRef],
  )

  const selectedCategories = formik.values.categories

  async function handleSubmit(values) {
    let geocoordinates
    if (values.street || values.postcode || values.place) {
      geocoordinates = await getGeo(
        values.street,
        values.postcode,
        values.place,
      )
    }

    try {
      await updateDoc(userRef, {
        ...formik.values,
        geocoordinates: geocoordinates,
      }).then(() => {
        toast.success(
          'Deine Änderungen wurden erfolgreich aktualisiert und gespeichert.',
          {
            position: 'top-center',
          },
        )
      })
    } catch {
      toast.error(
        'Leider konnten wir deine Änderungen nicht verarbeiten. Kontrolliere nochmal deine Eingaben und versuche es erneut.',
        {
          position: 'top-center',
        },
      )
    }
  }

  useEffect(() => {
    getShop()
  }, [])

  return (
    <>
      <p>
        Du möchtest deinen Shopnamen ändern, bist umgezogen oder hast andere
        Öffnungszeiten?
      </p>
      <p>
        Ändere in den Eingabefeldern deine gewünschten Daten. Jene Felder, die
        du nicht bearbeitest, bleiben so wie sie sind erhalten.
      </p>
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.input_container}>
          <Input
            name='shopname'
            type='text'
            label='Shop Name'
            placeholder='Mustershop'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.shopname}
          />
          {formik.touched.shopname && formik.errors.shopname && (
            <Error msg={formik.errors.shopname} />
          )}
        </div>
        <div className={styles.input_container}>
          <Input
            name='street'
            type='text'
            label='Straße Hausnummer'
            placeholder='Musterstraße 12'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.street}
          />
          {formik.touched.street && formik.errors.street && (
            <Error msg={formik.errors.street} />
          )}
        </div>
        <div className={styles.input_container}>
          <Input
            name='postcode'
            type='text'
            label='Postleitzahl'
            placeholder='5020'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.postcode}
          />
          {formik.touched.postcode && formik.errors.postcode && (
            <Error msg={formik.errors.postcode} />
          )}
        </div>
        <div className={styles.input_container}>
          <Input
            name='place'
            type='text'
            label='Ort'
            placeholder='Salzburg'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.place}
          />
          {formik.touched.place && formik.errors.place && (
            <Error msg={formik.errors.place} />
          )}
        </div>
        <div className={styles.input_container}>
          <Textarea
            name='openingHours'
            type='text'
            label='Öffnungszeiten'
            placeholder='von Montag bis Freitag,\nje von 12.30 - 17.45 Uhr'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.openingHours}
          />
          {formik.touched.openingHours && formik.errors.openingHours && (
            <Error msg={formik.errors.openingHours} />
          )}
        </div>
        <div className={styles.input_container}>
          <Input
            name='phone'
            type='text'
            label='Telefonnummer'
            placeholder='0650/232432434'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.phone}
          />
          {formik.touched.phone && formik.errors.phone && (
            <Error msg={formik.errors.phone} />
          )}
        </div>
        <div className={styles.input_container}>
          <Input
            name='link'
            type='text'
            label='Link'
            placeholder='https://MeineWebsite.at'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.link}
          />
          {formik.touched.link && formik.errors.link && (
            <Error msg={formik.errors.link} />
          )}
        </div>
        <p>Ausgewählte Kategorien werden farblich markiert</p>
        <div className={categoryStyles.category_container}>
          {CATEGORIES.map((category) => {
            return (
              <Category
                key={category.id}
                category={category}
                isSelected={selectedCategories.includes(category.id)}
                onCategoryClick={() => {
                  const currentCategory = selectedCategories.includes(
                    category.id,
                  )

                  if (currentCategory) {
                    formik.setFieldValue(
                      'categories',
                      selectedCategories.filter((id) => id !== category.id),
                    )
                  } else {
                    formik.setFieldValue('categories', [
                      ...selectedCategories,
                      category.id,
                    ])
                  }
                }}
              />
            )
          })}
        </div>
        <Button type='submit'>bestätigen und speichern</Button>
      </form>
    </>
  )
}
